/**
 * Activity Code Select Component
 *
 * Alanube-catalog-only: every selectable value comes from Alanube's economic
 * activity catalog (CIIU 4, `NNNN.N`), which is what electronic invoicing
 * validates against. Selection is 100% manual — nothing is auto-fetched nor
 * auto-applied. The optional "use the client's saved activity" shortcut (used
 * in the POS) runs only when the user presses its button. Legacy values not
 * in the catalog (old 6-digit CIIU 3 codes) are still displayed, with a warning
 * that they are omitted from electronic invoices, so the user replaces them.
 */

import { useMemo } from 'react'
import { AlertCircle, X, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/searchable-select'
import {
  useActivityCatalog,
} from '@/hooks/useValidActivityCodes'
import { t } from '@/i18n'

interface ActivityCodeSelectProps {
  identification: string
  value: string
  valueName: string
  onChange: (code: string, name: string) => void
  onBlur?: () => void
  disabled?: boolean
  // Actividad guardada en la ficha del cliente: cuando viene, se ofrece un
  // botón "Usar actividad del cliente" en lugar de precargarla.
  clientActivityCode?: string | null
  clientActivityName?: string | null
}

export function ActivityCodeSelect({
  value,
  valueName,
  onChange,
  onBlur,
  disabled,
  clientActivityCode,
  clientActivityName,
}: ActivityCodeSelectProps) {
  const handleUserChange = (code: string, name: string) => {
    onChange(code, name)
  }

  // Alanube catalog (the ONLY selectable source)
  const { data: catalogData, isLoading: catalogLoading } = useActivityCatalog()
  const catalog = catalogData?.activities ?? []
  const catalogAvailable = catalog.length > 0

  // Build a code→name map from catalog for quick lookup
  const catalogMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const item of catalog) {
      map.set(item.code, item.name)
    }
    return map
  }, [catalog])

  const clientActivityInCatalog =
    !!clientActivityCode && catalogMap.has(clientActivityCode)
  const onUseClientActivity = () => {
    if (!clientActivityCode || !clientActivityInCatalog) return
    handleUserChange(
      clientActivityCode,
      catalogMap.get(clientActivityCode) ?? clientActivityName ?? ''
    )
    onBlur?.()
  }

  // Options come exclusively from the catalog. A legacy value not in the
  // catalog is prepended for DISPLAY (so the select doesn't look empty), and
  // flagged below with a warning so the user replaces it.
  const valueNotInCatalog = !!value && catalogAvailable && !catalogMap.has(value)
  const options: SearchableSelectOption[] = useMemo(() => {
    const opts: SearchableSelectOption[] = catalog.map((item) => ({
      value: item.code,
      label: `${item.code} — ${item.name}`,
    }))
    if (value && !catalogMap.has(value)) {
      opts.unshift({
        value,
        label: valueName ? `${value} — ${valueName}` : value,
      })
    }
    return opts
  }, [catalog, catalogMap, value, valueName])

  // Loading state
  if (catalogLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Spinner size="sm" />
        <span>{t('activities.catalogLoading')}</span>
      </div>
    )
  }

  // Catalog unavailable: selection is catalog-only, so there is nothing to
  // pick from. Show the current value (if any) with a clear option.
  if (!catalogAvailable) {
    return (
      <div className="space-y-1.5">
        {value && (
          <div className="flex items-center gap-2 text-sm">
            <span className="truncate">{valueName ? `${value} — ${valueName}` : value}</span>
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  handleUserChange('', '')
                  onBlur?.()
                }}
                title={t('activities.clearSelection')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('activities.catalogUnavailable')}</p>
      </div>
    )
  }

  const showClientButton =
    !disabled && clientActivityInCatalog && clientActivityCode !== value

  return (
    <div className="space-y-2">
      {showClientButton && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={onUseClientActivity}
        >
          <User className="h-3 w-3 mr-1.5" />
          {t('activities.useClientActivity')}
        </Button>
      )}

      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0">
          <SearchableSelect
            options={options}
            value={value}
            onValueChange={(newValue) => {
              const name = catalogMap.get(newValue) ?? valueName
              handleUserChange(newValue, name)
              onBlur?.()
            }}
            placeholder={t('activities.searchPlaceholder')}
            searchPlaceholder={t('activities.searchPlaceholder')}
            emptyMessage={t('activities.noResults')}
            disabled={disabled}
          />
        </div>
        {value && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => {
              handleUserChange('', '')
              onBlur?.()
            }}
            title={t('activities.clearSelection')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {valueNotInCatalog && (
        <p className="flex items-start gap-1.5 text-xs text-yellow-700 dark:text-yellow-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {t('activities.notInAlanubeCatalogWarning')}
        </p>
      )}
    </div>
  )
}
